<template>
    <div class="video-player">
        <video v-if="source.kind == 'file'" ref="video" :src="source.src" preload="auto" playsinline controls
            @canplay="set_ready" @loadedmetadata="on_metadata" @error="on_error"></video>
        <div v-else class="video-player-embed"><div ref="embed"></div></div>

        <button v-if="muted" type="button" class="btn btn-light video-player-unmute" @click="unmute">
            <i class="bi bi-volume-mute-fill me-1"></i>Activar sonido
        </button>
        <button v-if="needs_tap" type="button" class="video-player-tap" @click="tap_to_play">
            <i class="bi bi-play-circle-fill"></i>
        </button>
    </div>
</template>

<script>
import VimeoPlayer from '@vimeo/player'

// The IFrame API is a global script that calls window.onYouTubeIframeAPIReady
// once - load it a single time and share it between every player.
let youtube_api = null;
function load_youtube_api(){
    if(window.YT && window.YT.Player)
        return Promise.resolve(window.YT);
    if(!youtube_api){
        youtube_api = new Promise((resolve, reject) => {
            const previous = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if(previous) previous();
                resolve(window.YT);
            };
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            tag.onerror = () => { youtube_api = null; reject(new Error('No se ha podido cargar YouTube')); };
            document.head.appendChild(tag);
        });
    }
    return youtube_api;
}

// One interface over a plain <video>, YouTube and Vimeo: emits `ready` once
// the player can start (also on failure - a broken video must not hold the
// rest of the room waiting, see sv._await_media_ready), and play(offset)
// starts it, `offset` seconds in for someone joining late. Browsers often
// refuse to autoplay with sound, so play() falls back to muted playback plus
// an "Activar sonido" button, and to a big tap-to-play button as a last resort.
export default {
    name: 'VideoPlayer',
    props: {
        source: { type: Object, required: true },
    },
    emits: ['ready'],
    data(){
        return {
            ready: false,
            muted: false,
            needs_tap: false,
            pending_offset: 0,
        };
    },
    mounted(){
        // Not reactive on purpose - third-party player objects don't belong
        // in Vue's proxies.
        this.player = null;
        this.metadata_timer = null;
        this.check_timer = null;

        if(this.source.kind == 'youtube')
            this.mount_youtube();
        else if(this.source.kind == 'vimeo')
            this.mount_vimeo();
    },
    beforeUnmount(){
        clearTimeout(this.metadata_timer);
        clearTimeout(this.check_timer);
        if(this.player && this.player.destroy){
            try{ this.player.destroy(); }catch(e){}
        }
        this.player = null;
    },
    methods: {
        set_ready(){
            if(this.ready) return;
            this.ready = true;
            clearTimeout(this.metadata_timer);
            this.$emit('ready');
        },
        // iOS Safari doesn't buffer video data before a user gesture, so
        // `canplay` may never come - metadata plus a short grace is as close
        // to "loaded" as it gets there.
        on_metadata(){
            clearTimeout(this.metadata_timer);
            this.metadata_timer = setTimeout(this.set_ready, 3000);
        },
        on_error(){
            console.error("[VideoPlayer] Error cargando el vídeo", this.source);
            this.set_ready();
        },
        async mount_youtube(){
            try{
                const YT = await load_youtube_api();
                if(!this.$refs.embed) return;
                this.player = new YT.Player(this.$refs.embed, {
                    videoId: this.source.id,
                    width: '100%',
                    height: '100%',
                    playerVars: { playsinline: 1, rel: 0, modestbranding: 1 },
                    events: {
                        onReady: this.set_ready,
                        onError: this.on_error,
                    },
                });
            }catch(e){
                this.on_error();
            }
        },
        mount_vimeo(){
            this.player = new VimeoPlayer(this.$refs.embed, {
                id: this.source.id,
                playsinline: true,
            });
            this.player.ready().then(this.set_ready).catch(this.on_error);
        },
        play(offset = 0){
            this.needs_tap = false;
            switch(this.source.kind){
                case 'file': return this.play_file(offset);
                case 'youtube': return this.play_youtube(offset);
                case 'vimeo': return this.play_vimeo(offset);
            }
        },
        play_file(offset){
            const video = this.$refs.video;
            if(!video) return;
            if(offset > 0) video.currentTime = offset;
            video.play().catch(() => {
                video.muted = true;
                this.muted = true;
                return video.play();
            }).catch(() => {
                this.pending_offset = offset;
                this.needs_tap = true;
            });
        },
        // The IFrame API gives no promise to catch a blocked autoplay on -
        // check shortly after whether it actually started, and retry muted.
        play_youtube(offset){
            const player = this.player;
            if(!player || !player.playVideo) return;
            if(offset > 0) player.seekTo(offset, true);
            player.playVideo();
            clearTimeout(this.check_timer);
            this.check_timer = setTimeout(() => {
                if(this.player !== player) return;
                const state = player.getPlayerState();
                if(state === window.YT.PlayerState.PLAYING || state === window.YT.PlayerState.BUFFERING) return;
                player.mute();
                this.muted = true;
                player.playVideo();
                this.check_timer = setTimeout(() => {
                    if(this.player !== player) return;
                    if(player.getPlayerState() !== window.YT.PlayerState.PLAYING){
                        this.pending_offset = offset;
                        this.needs_tap = true;
                    }
                }, 1500);
            }, 1200);
        },
        play_vimeo(offset){
            const player = this.player;
            if(!player) return;
            const seek = offset > 0 ? player.setCurrentTime(offset).catch(() => {}) : Promise.resolve();
            seek.then(() => player.play()).catch(() => {
                this.muted = true;
                return player.setMuted(true).then(() => player.play());
            }).catch(() => {
                this.pending_offset = offset;
                this.needs_tap = true;
            });
        },
        tap_to_play(){
            this.muted = false;
            if(this.source.kind == 'file' && this.$refs.video) this.$refs.video.muted = false;
            if(this.source.kind == 'youtube' && this.player && this.player.unMute) this.player.unMute();
            if(this.source.kind == 'vimeo' && this.player) this.player.setMuted(false).catch(() => {});
            this.play(this.pending_offset);
        },
        unmute(){
            this.muted = false;
            if(this.source.kind == 'file' && this.$refs.video)
                this.$refs.video.muted = false;
            else if(this.source.kind == 'youtube' && this.player){
                this.player.unMute();
                this.player.setVolume(100);
            }
            else if(this.source.kind == 'vimeo' && this.player)
                this.player.setMuted(false).catch(() => {});
        },
    }
}
</script>

<style scoped>
    .video-player{
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 9;
        max-height: 70vh;
        background-color: #000;
        border-radius: 8px;
        overflow: hidden;
    }
    .video-player video,
    .video-player-embed,
    .video-player-embed :deep(iframe),
    .video-player-embed :deep(div){
        width: 100%;
        height: 100%;
        border: 0;
    }
    .video-player-unmute{
        position: absolute;
        top: 12px;
        left: 12px;
        z-index: 2;
        font-weight: 700;
    }
    .video-player-tap{
        position: absolute;
        inset: 0;
        z-index: 3;
        border: 0;
        background-color: rgba(0, 0, 0, 0.45);
        color: #fff;
        font-size: 5rem;
    }
</style>
